/** Persistent notice sourced from regional configuration.
 *
 * Collapsed it's a single truncating line so it never wraps or pushes the
 * header's mode toggle off-centre. Clicking (or Enter/Space) opens the full
 * notice in a floating panel that overlays the map — absolute-positioned so it
 * never reflows the header — and closes on click-away or Escape. */

import { useEffect, useId, useRef, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import type { ContactConfig } from "../types";

interface WarningBannerProps {
  text: string;
  contact: ContactConfig;
  /** Extra classes for the outer container. */
  className?: string;
}

const WarnIcon = ({ className }: { className: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M12 3 2.7 20h18.6L12 3Z" />
    <path d="M12 9v5m0 3h.01" />
  </svg>
);

export function WarningBanner({ text, contact, className = "" }: WarningBannerProps) {
  useLanguageContext();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const emailLink = contact.email ? (
    <a
      href={`mailto:${contact.email}`}
      className="font-semibold text-red-800 underline decoration-red-600/60 underline-offset-2 hover:text-red-600 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
    >
      {contact.email}
    </a>
  ) : null;

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      {/* Collapsed trigger — one truncating line. The whole chip is the
          disclosure control; the email is shown as plain text here (a real,
          clickable link lives in the expanded panel) so there's no interactive
          element nested inside the button. */}
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={contact.email ? `${text} ${contact.email}` : text}
        className="flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md border border-red-600 bg-white px-3 py-1.5 text-left text-red-800 shadow-sm transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 motion-reduce:transition-none"
      >
        <WarnIcon className="h-4 w-4 shrink-0 text-red-600" />
        <span className="min-w-0 truncate text-xs font-semibold leading-snug sm:text-sm">
          {text}
        </span>
        {contact.email ? (
          <span className="shrink-0 text-xs font-semibold underline decoration-red-600/60 underline-offset-2 sm:text-sm">
            {contact.email}
          </span>
        ) : null}
      </button>

      {/* Expanded panel — full text, wrapped. Absolute so it overlays the map
          instead of growing the header. */}
      {open ? (
        <div
          id={panelId}
          role="status"
          className="absolute right-0 top-full z-30 mt-1.5 w-[min(24rem,90vw)] rounded-md border border-red-600 bg-white px-3 py-2.5 text-red-800 shadow-panel"
        >
          <div className="flex items-start gap-2">
            <WarnIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-xs font-semibold leading-snug sm:text-sm">
              {text}
              {emailLink ? (
                <>
                  {" "}
                  {emailLink}
                </>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
